import { getDeviceId } from '@amplitude/analytics-browser';
import { getUser, getUserAndTokenFromStorage, isBrowser } from '@firsttable/functions';
import { useViewportTier } from '@firsttable/web-theme';
import type { GrowthBook, TrackingCallback } from '@growthbook/growthbook';
import { GrowthBookProvider as DefaultGrowthBookProvider } from '@growthbook/growthbook-react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';
import {
  buildGrowthBookAttributes,
  createDisabledGrowthBook,
  createSessionGrowthBook,
  isGrowthBookConfigured,
  restoreGrowthBookExperiments,
  withdrawGrowthBookConsent,
} from '@/lib/analytics/growthbook';
import { useAmplitudeEnabled } from '@/lib/analytics/amplitude/useAmplitudeEnabled';
import {
  getGrowthBookDeviceId,
  persistGrowthBookFallbackDeviceId,
} from '@/lib/analytics/growthbook/deviceId';
import { useConsent, useConsentHandler } from '@/lib/consent';
import Logger from '@/lib/logger';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useGrowthBookEventsTracking } from '../../hooks/useGrowthBookEventsTracking';
import { getPermissionsListFromToken } from '../../utils/getPermissionsListFromToken';

const GrowthBookEventsTracker = () => {
  useGrowthBookEventsTracking();
  return null;
};

const getSessionGrowthBook = (
  growthbookRef: MutableRefObject<GrowthBook | null>,
  apiHost: string,
  clientKey: string,
  trackingCallback: TrackingCallback
): GrowthBook => {
  if (growthbookRef.current) {
    return growthbookRef.current;
  }

  growthbookRef.current = isGrowthBookConfigured(apiHost, clientKey)
    ? createSessionGrowthBook({ apiHost, clientKey, trackingCallback })
    : createDisabledGrowthBook();

  return growthbookRef.current;
};

export const GrowthBookProvider = ({ apiHost, clientKey, siteId, children }) => {
  const router = useRouter();
  const { track: trackEvent } = useAnalytics();
  const { allowed: consentAllowed, hydrated: consentHydrated } = useConsent();
  const amplitudeEnabled = useAmplitudeEnabled();
  const loggerRef = useRef(new Logger('Analytics'));
  const trackEventRef = useRef(trackEvent);
  const growthbookRef = useRef<GrowthBook | null>(null);

  useEffect(() => {
    trackEventRef.current = trackEvent;
  }, [trackEvent]);

  const cityUrl =
    isBrowser && window.location.href
      ? window.location.href.replace(/(^\w+:|^)\/\//, '').split('/')
      : '';
  const city =
    Array.isArray(cityUrl) && cityUrl.length > 1 && cityUrl[1] !== 'booking' ? cityUrl[1] : '';

  const user = getUser();
  const { token } = getUserAndTokenFromStorage();
  const permissionsList = getPermissionsListFromToken(token);
  const userTargetingCandidate = {
    ...user,
    permissionsList,
  };
  const routePathname = router.pathname;
  const routeAsPath = router.asPath;
  const userTargetingKey = JSON.stringify(userTargetingCandidate);
  const userTargeting = useRef(userTargetingCandidate);
  if (userTargeting.current == null || JSON.stringify(userTargeting.current) !== userTargetingKey) {
    userTargeting.current = userTargetingCandidate;
  }
  const amplitudeDeviceId = amplitudeEnabled ? getDeviceId() : undefined;
  const growthbookDeviceId = getGrowthBookDeviceId({
    amplitudeDeviceId,
    canUseStoredDeviceId: consentHydrated && consentAllowed,
    consentHydrated,
  });
  // Viewport tier for experiment targeting so bucketing stays aligned with the CSS layout pivots.
  const viewportTier = useViewportTier();
  const growthbookConfigured = isGrowthBookConfigured(apiHost, clientKey);

  const trackingCallback = useCallback<TrackingCallback>((experiment, result) => {
    const experimentViewedProperties = {
      experiment_name: experiment.key,
      variant_id: result.variationId,
      variant_name: result.name,
      variant_value: result.value,
      user_in_experiment: result.inExperiment,
      growthbook_experiment_id: experiment.key,
      growthbook_variation_id: result.key,
    };

    loggerRef.current.debug('Experiment Viewed', experimentViewedProperties);
    trackEventRef.current('experiment_viewed', experimentViewedProperties);
  }, []);

  const growthbook = getSessionGrowthBook(growthbookRef, apiHost, clientKey, trackingCallback);

  useConsentHandler('growthbook', ({ allowed, hydrated }) => {
    if (!hydrated || !growthbookConfigured) {
      return;
    }

    if (allowed) {
      restoreGrowthBookExperiments(growthbook, trackingCallback);
      return;
    }

    withdrawGrowthBookConsent(growthbook, trackingCallback);
  });

  useEffect(() => {
    if (!growthbookConfigured) {
      return undefined;
    }

    growthbook.init();
  }, [growthbook, growthbookConfigured]);

  useEffect(() => {
    if (!consentHydrated || !consentAllowed) {
      return;
    }

    persistGrowthBookFallbackDeviceId();
  }, [consentAllowed, consentHydrated]);

  useEffect(() => {
    if (!growthbookConfigured) {
      return undefined;
    }

    const applyAttributes = () => {
      growthbook
        .setAttributes(
          buildGrowthBookAttributes({
            siteId,
            city,
            router,
            privateAttributesEnabled: consentAllowed,
            deviceId: growthbookDeviceId,
            amplitudeDeviceId,
            viewportTier,
            userTargeting: userTargeting.current,
          })
        )
        .catch((error) => {
          loggerRef.current.error('Error setting GrowthBook attributes', error);
        });
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (growthbookDeviceId) {
      applyAttributes();
    } else {
      timeoutId = setTimeout(applyAttributes, 0);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    growthbook,
    router,
    routePathname,
    routeAsPath,
    growthbookDeviceId,
    amplitudeDeviceId,
    viewportTier,
    city,
    siteId,
    growthbookConfigured,
    consentAllowed,
    userTargetingKey,
  ]);

  return (
    <DefaultGrowthBookProvider growthbook={growthbook}>
      {growthbookConfigured && <GrowthBookEventsTracker />}
      {children}
    </DefaultGrowthBookProvider>
  );
};
